import React, { useState } from 'react';
import { partitionComponents, isComponent, useTheme, ThemeProvider } from '@redsift/design-system';
import { ToggletipContent } from '../toggletip-content';
import { ToggletipTrigger } from '../toggletip-trigger';

import { ToggletipContext } from './context';
import { ToggletipPlacement, ToggletipProps } from './types';
import { useToggletip } from './useToggletip';

const COMPONENT_NAME = 'Toggletip';
const CLASSNAME = 'redsift-toggletip';

/**
 * Toggletip displays supplementary content toggled by click/tap.
 * Similar to Tooltip but triggered on click rather than hover,
 * making it accessible for touch devices and keyboard users.
 *
 * Use for help text, definitions, or supplementary info that users
 * explicitly request.
 *
 * Placements: `top`, `top-start`, `top-end`, `bottom`, `bottom-start`,
 * `bottom-end`, `left`, `left-start`, `left-end`, `right`, `right-start`,
 * `right-end`
 *
 * @example
 * // Basic toggletip with icon trigger
 * <Toggletip>
 *   <Toggletip.Trigger>
 *     <IconButton icon={mdiHelp} aria-label="Help" />
 *   </Toggletip.Trigger>
 *   <Toggletip.Content>
 *     Click here for more details about this feature.
 *   </Toggletip.Content>
 * </Toggletip>
 *
 * @example
 * // With custom placement and rich content
 * <Toggletip placement="bottom-start">
 *   <Toggletip.Trigger>
 *     <Link>What is this?</Link>
 *   </Toggletip.Trigger>
 *   <Toggletip.Content>
 *     <Heading level={4}>Definition</Heading>
 *     <Text>SPF (Sender Policy Framework) is an email authentication method...</Text>
 *   </Toggletip.Content>
 * </Toggletip>
 *
 * @example
 * // Controlled toggletip
 * <Toggletip isOpen={showHelp} onOpen={setShowHelp}>
 *   <Toggletip.Trigger>
 *     <Icon icon={mdiInformationOutline} />
 *   </Toggletip.Trigger>
 *   <Toggletip.Content>Help content here</Toggletip.Content>
 * </Toggletip>
 *
 * @example
 * // Modal toggletip with sizing
 * <Toggletip isModal width={300}>
 *   {...}
 * </Toggletip>
 */
export const BaseToggletip: React.FC<ToggletipProps> & {
  displayName?: string;
  className?: string;
} = (props) => {
  const {
    children,
    color,
    defaultOpen,
    isModal = false,
    isOpen,
    maxWidth,
    minWidth,
    offset,
    onOpen,
    overrideDisplayName,
    placement = ToggletipPlacement.top,
    role = 'dialog',
    theme: propsTheme,
    toggletipId,
    triggerClassName,
    width,
  } = props;

  const theme = useTheme(propsTheme);
  const [hideInsteadOfClose, setHideInsteadOfClose] = useState(false);

  const toggletip = useToggletip({
    color,
    defaultOpen,
    isModal,
    isOpen,
    maxWidth,
    minWidth,
    offset,
    onOpen,
    placement,
    role,
    toggletipId,
    triggerClassName,
    setHideInsteadOfClose,
    hideInsteadOfClose,
    width,
  });

  const [[trigger], [content]] = partitionComponents(React.Children.toArray(children), [
    isComponent(overrideDisplayName?.trigger ?? 'ToggletipTrigger'),
    isComponent(overrideDisplayName?.content ?? 'ToggletipContent'),
  ]);

  return (
    <ThemeProvider value={{ theme }}>
      <ToggletipContext.Provider value={toggletip}>
        {trigger && isComponent(overrideDisplayName?.trigger ?? 'ToggletipTrigger')(trigger) ? trigger : null}
        {content && isComponent(overrideDisplayName?.content ?? 'ToggletipContent')(content) ? content : null}
      </ToggletipContext.Provider>
    </ThemeProvider>
  );
};
BaseToggletip.className = CLASSNAME;
BaseToggletip.displayName = COMPONENT_NAME;

export const Toggletip = Object.assign(BaseToggletip, {
  Trigger: ToggletipTrigger,
  Content: ToggletipContent,
});
